import { memo } from "react";
import { cn } from "@repo/design-system/utils";
import { useProfile } from "@/queries/customer/customer";
import { SupportWhatsAppLink } from "@/components/support/SupportWhatsAppLink";
import { CheckCircle, RotateCcw } from "lucide-react";

export interface TrustBadge {
  icon: React.ReactNode;
  label: React.ReactNode;
}

export interface TrustBadgesProps {
  /** Custom badges (optional, uses defaults if not provided) */
  badges?: TrustBadge[];
  /** Optional className */
  className?: string;
}

// True of every way an order is paid - card, bank transfer, or billed by the
// supplier - since the summary renders before the payment method is known.
// The bank-transfer wording that was here read wrong on card and
// supplier-collected checkouts (KAN-230); the transfer specifics belong to
// the bank transfer instructions on the payment step.
// The "Support" word opens the WhatsApp support chat. The tap area is padded
// to 44px tall without moving the line, since this list sits on a phone.
function SupportBadgeLabel() {
  const { data } = useProfile();
  return (
    <>
      <SupportWhatsAppLink
        profile={data?.customer}
        className="-my-3 py-3 underline decoration-gray-400 underline-offset-2 hover:text-gray-900"
      >
        Support
      </SupportWhatsAppLink>{" "}
      available if something goes wrong
    </>
  );
}

const defaultBadges: TrustBadge[] = [
  {
    icon: <CheckCircle className="h-4 w-4" />,
    label: <SupportBadgeLabel />
  },
  {
    icon: <RotateCcw className="h-4 w-4" />,
    label: "Returns subject to supplier policy"
  }
];

/**
 * Trust badges section showing security and guarantee information.
 * Displayed at the bottom of the order summary.
 */
export const TrustBadges = memo(function TrustBadges({
  badges = defaultBadges,
  className
}: TrustBadgesProps) {
  return (
    <div
      className={cn("border-t border-gray-200 bg-gray-50 px-6 py-4", className)}
    >
      {/* Header */}
      <div className="mb-3">
        <span className="text-sm font-medium text-gray-700">
          What to Expect
        </span>
      </div>

      {/* Badge List */}
      <ul className="space-y-2">
        {badges.map((badge, index) => (
          <li key={index} className="flex items-center gap-2 text-sm">
            <span className="text-green-600" aria-hidden="true">
              {badge.icon}
            </span>
            <span className="text-gray-600">{badge.label}</span>
          </li>
        ))}
      </ul>
    </div>
  );
});
